import { useState } from 'react';
import {
  Box, Typography, Select, MenuItem, TextField, FormControl, InputLabel, Collapse, Button, Link,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import type { PostbackFilter } from '../../services/postbacks.service';
import { validateFilter } from './postbackFilterSchema';

const PAYOUT_OPS = [
  { value: '', label: 'Any revenue' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'eq', label: '=' },
  { value: 'between', label: 'between' },
];

function prune(filter: PostbackFilter): PostbackFilter | undefined {
  const out: PostbackFilter = {};
  if (filter.payout) out.payout = filter.payout;
  if (filter.query_params?.length) out.query_params = filter.query_params;
  if (filter.$or?.length) out.$or = filter.$or;
  return Object.keys(out).length ? out : undefined;
}

export default function PostbackFilterEditor({ value, onChange }: {
  value?: PostbackFilter;
  onChange: (filter: PostbackFilter | undefined) => void;
}) {
  const payout = value?.payout;
  const isBetween = payout?.op === 'between';
  const range = Array.isArray(payout?.value) ? payout.value as [number, number] : [0, 0];

  const rest = { ...(value || {}) };
  delete rest.payout;
  const [advOpen, setAdvOpen] = useState(false);
  const [raw, setRaw] = useState(() => (Object.keys(rest).length ? JSON.stringify(rest, null, 2) : ''));
  const [rawError, setRawError] = useState<string | null>(null);

  const setPayout = (p: PostbackFilter['payout']) => onChange(prune({ ...value, payout: p }));

  const commitRaw = (text: string) => {
    setRaw(text);
    if (!text.trim()) {
      setRawError(null);
      onChange(prune({ ...(payout ? { payout } : {}) }));
      return;
    }
    try {
      const parsed = JSON.parse(text);
      const errors = validateFilter(parsed);
      if (errors.length) {
        setRawError(errors.slice(0, 3).join(' · '));
        return;
      }
      setRawError(null);
      onChange(prune({ ...parsed, ...(payout ? { payout } : {}) }));
    } catch (err) {
      setRawError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Send condition (empty = always send)
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Revenue</InputLabel>
          <Select
            label="Revenue"
            value={payout?.op || ''}
            onChange={(e) => {
              const op = e.target.value as string;
              if (!op) return setPayout(undefined);
              setPayout({ op: op as never, value: op === 'between' ? [0, 0] : 0 });
            }}
          >
            {PAYOUT_OPS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>

        {payout && !isBetween && (
          <TextField
            size="small" type="number" label="Value" sx={{ width: 130 }}
            value={typeof payout.value === 'number' ? payout.value : 0}
            onChange={(e) => setPayout({ ...payout, value: Number(e.target.value) })}
          />
        )}

        {payout && isBetween && (
          <>
            <TextField
              size="small" type="number" label="From" sx={{ width: 110 }} value={range[0]}
              onChange={(e) => setPayout({ ...payout, value: [Number(e.target.value), range[1]] })}
            />
            <TextField
              size="small" type="number" label="To" sx={{ width: 110 }} value={range[1]}
              onChange={(e) => setPayout({ ...payout, value: [range[0], Number(e.target.value)] })}
            />
          </>
        )}
      </Box>

      <Box>
        <Link component="button" type="button" variant="caption" underline="hover"
          onClick={() => setAdvOpen(o => !o)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
          {advOpen ? <ExpandLess sx={{ fontSize: 15 }} /> : <ExpandMore sx={{ fontSize: 15 }} />}
          Advanced rules{Object.keys(rest).length ? ' (set)' : ''}
        </Link>
        <Collapse in={advOpen}>
          <TextField
            fullWidth multiline minRows={3} size="small" sx={{ mt: 0.5 }}
            value={raw}
            onChange={(e) => commitRaw(e.target.value)}
            error={!!rawError}
            placeholder={'{\n  "query_params": [{ "name": "sub1", "op": "equals", "value": "abc" }]\n}'}
            helperText={rawError
              || 'Rules are ANDed. query_params ops: equals, in, regex, numberRange. $or takes a list of sub-filters, one of which must match.'}
            slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.78rem' } } }}
          />
          {!!value && (
            <Button size="small" onClick={() => { setRaw(''); setRawError(null); onChange(undefined); }}>
              Clear filter
            </Button>
          )}
        </Collapse>
      </Box>
    </Box>
  );
}
